/**
 * Arabian Flames - original-design runtime, revision 2.
 *
 * Rebuilt from the uploaded theme's interaction contract. Presentation stays
 * in the original Liquid/CSS. Do not set fonts, replace image markup, change
 * breakpoints, or introduce new layout classes here.
 *
 * Original jQuery/Slick/GSAP behavior is retained. Optional libraries are
 * requested only when a corresponding component exists, not on every page.
 */
(() => {
  'use strict';

  if (window.ArabianFlamesOriginalRuntime) {
    window.ArabianFlamesOriginalRuntime.refresh(document);
    return;
  }

  const runtimeTag = document.querySelector('script[data-af-runtime]');
  const pendingLibraries = new Map();
  function loadOptionalLibrary(name, url) {
    if (!url) return Promise.reject(new Error('Missing optional library URL'));
    if (pendingLibraries.has(name)) return pendingLibraries.get(name);
    const promise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = url;
      script.async = true;
      script.dataset.afOptionalLibrary = name;
      script.onload = () => resolve();
      script.onerror = () => { script.remove(); pendingLibraries.delete(name); reject(new Error(name + ' unavailable')); };
      document.head.appendChild(script);
    });
    pendingLibraries.set(name, promise);
    return promise;
  }
  function loadNeededLibraries(root) {
    if (selectWithin(root, '.feature_slider').length && window.jQuery && !window.jQuery.fn.slick) {
      loadOptionalLibrary('slick', runtimeTag?.dataset.slickUrl)
        .then(() => initialiseSliders(document)).catch(() => {});
    }
    if (selectWithin(root, '.scrolling-track').length && !window.gsap) {
      loadOptionalLibrary('gsap', runtimeTag?.dataset.gsapUrl)
        .then(() => initialiseAnnouncements(document)).catch(() => {});
    }
  }

  const sliders = new Set();
  const tracks = new Map();
  const timers = new Map();
  const popupSeen = new WeakSet();
  const namespace = '.afOriginalDesign';
  let resizeFrame = 0;
  let delegatesBound = false;

  function selectWithin(root, selector) {
    const result = Array.from(root.querySelectorAll(selector));
    if (root.nodeType === 1 && root.matches(selector)) result.unshift(root);
    return result;
  }

  function initialiseSliders(root) {
    const $ = window.jQuery;
    if (!$ || !$.fn.slick) return;

    selectWithin(root, '.feature_slider').forEach((node) => sliders.add(node));
    sliders.forEach((node) => {
      if (!node.isConnected) {
        sliders.delete(node);
        return;
      }
      const slider = $(node);
      const ready = slider.hasClass('slick-initialized');
      // Keep the uploaded theme's exact 750px boundary and Slick options.
      if ($(window).width() > 750 && !ready) {
        slider.slick({
          dots: false,
          arrows: false,
          infinite: false,
          speed: 300,
          slidesToShow: 2,
          slidesToScroll: 1
        });
      } else if ($(window).width() <= 750 && ready) {
        slider.slick('unslick');
      }
    });
  }

  function initialiseAnnouncements(root) {
    const gsap = window.gsap;
    if (!gsap || typeof gsap.to !== 'function') return;

    selectWithin(root, '.scrolling-track').forEach((node) => {
      if (tracks.has(node)) return;
      tracks.set(node, gsap.to(node, {
        xPercent: -50,
        duration: 120,
        ease: 'none',
        repeat: -1
      }));
    });
  }

  function closePopup() {
    document.body.classList.add('inactive_pop');
    document.querySelectorAll('.newsletter_popup_wrap').forEach((node) => {
      node.classList.add('hide_active_pop');
    });
  }

  function initialisePopups(root) {
    selectWithin(root, '.newsletter_popup_wrap').forEach((node) => {
      if (popupSeen.has(node)) return;
      popupSeen.add(node);
      timers.set(node, window.setTimeout(() => {
        timers.delete(node);
        if (!node.isConnected) return;
        // Retain the original delay, classes and per-page behaviour.
        // Do not lock an empty homepage when its popup section was removed.
        if (document.body.classList.contains('template_name_index')) {
          document.body.classList.add('opened_popup');
        }
        node.classList.add('show_active_pop');
      }, 10000));
    });
  }

  function bindDelegates() {
    if (delegatesBound) return;
    const $ = window.jQuery;
    if (!$) return;
    delegatesBound = true;

    $(document).off(namespace);
    $(document).on('click' + namespace, '.acc__title', function (event) {
      const title = $(this);
      const panel = title.closest('.acc__card').find('.acc__panel');
      title.closest('.acc').find('.acc__panel').not(panel).slideUp();
      if (title.hasClass('active')) {
        title.removeClass('active');
      } else {
        title.closest('.acc').find('.acc__title.active').removeClass('active');
        title.addClass('active');
      }
      // Match the original jQuery animation, including its queue handling.
      panel.stop(false, true).slideToggle();
      event.preventDefault();
    });

    $(document).on('click' + namespace, '.footer-block__heading', function () {
      $(this).next('.footer-block__details-content').stop(true, true).slideToggle();
      $(this).toggleClass('active');
    });
  }

  function refresh(root = document) {
    bindDelegates();
    loadNeededLibraries(root);
    initialiseSliders(root);
    initialiseAnnouncements(root);
    initialisePopups(root);
  }

  function unload(root) {
    const $ = window.jQuery;
    sliders.forEach((node) => {
      if (root !== node && !root.contains(node)) return;
      if ($ && $.fn.slick && $(node).hasClass('slick-initialized')) $(node).slick('unslick');
      sliders.delete(node);
    });
    tracks.forEach((tween, node) => {
      if (root !== node && !root.contains(node)) return;
      if (tween && typeof tween.kill === 'function') tween.kill();
      tracks.delete(node);
    });
    timers.forEach((id, node) => {
      if (root !== node && !root.contains(node)) return;
      window.clearTimeout(id);
      timers.delete(node);
      popupSeen.delete(node);
    });
    // A removed popup must not leave the theme editor preview scroll-locked.
    if (selectWithin(root, '.newsletter_popup_wrap').length) {
      document.body.classList.remove('opened_popup');
    }
  }

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('.close_email_pop')) closePopup();

    const toggle = event.target.closest('.custom-password-toggle');
    if (!toggle) return;
    const field = toggle.closest('.custom-password-wrapper')?.querySelector('input');
    if (!field) return;
    const show = field.type === 'password';
    field.type = show ? 'text' : 'password';
    toggle.textContent = show ? 'Hide' : 'Show';
  });

  window.addEventListener('resize', () => {
    if (resizeFrame) window.cancelAnimationFrame(resizeFrame);
    resizeFrame = window.requestAnimationFrame(() => {
      resizeFrame = 0;
      initialiseSliders(document);
    });
  });
  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:unload', (event) => unload(event.target));
  document.addEventListener('shopify:section:reorder', () => refresh(document));

  window.ArabianFlamesOriginalRuntime = Object.freeze({ refresh });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => refresh(document), { once: true });
  } else {
    refresh(document);
  }
})();
